import { use, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from 'react-leaflet';
import { AncientRoadsLayer } from './map/AncientRoadsLayer';
import { RomanProvincesLayer } from './map/RomanProvincesLayer';
import { AncientEmpiresLayer } from './map/AncientEmpiresLayer';
import { type LatLngExpression, type Map as LeafletMap, type Marker as LeafletMarker } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { type TransitionStage } from '@/lib/hooks/use-geographical-transition';
import { AppDataContext } from '@/lib/app-data-hydration';
import { useHistoricalEventsByPassageData } from '@/data/historical-events/historical-events-by-passage-data-context';
import { HistoricalEventsByBookDataContext } from '@/data/historical-events/historical-events-by-book-data-context';
import { HistoricalEventsDataContext } from '@/data/historical-events/historical-events-data-context';
import { PassageDataContext } from '@/data/passage/passage-data-context';
import { PlacesDataContext } from '@/data/places/places-data-context';
import { usePlacesByPassageData } from '@/data/places/places-by-passage-data-context';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin, SlidersHorizontal } from 'lucide-react';
import { useUrl } from 'crossroad';
import { MapTimelineControl } from './map/MapTimelineControl';
import { MapLegend } from './map/MapLegend';
import { MAP_STYLES } from './map/MapStyleControl';
import { combinePassageAndBookEvents, getRelevantEventTitles } from './map/map-helpers';
import { convertPlaceToEntity } from './map/ancient-world-map-utils';
import { MARKER_POPUP_ANIMATION_DELAY } from './map/map-constants';
import { cn } from '@/lib/utils';
import { calculatePassageDate } from './map/passage-date-utils';
import { initializeTimelineAndFilters } from './map/timeline-initialization-utils';
import { filterMapData } from './map/map-filtering-utils';
import { calculateInitialMapPosition } from './map/map-position-utils';
import { MapBoundsUpdater, MapColorFilter, MapClickHandler, MapViewSetter, MapAnimationStopper } from './map/MapControls';
import { createPlaceIcon, createEventIcon, createMemoizedPlaceIcon } from './map/map-icon-utils';
import { PlaceMarkerPopup } from './map/PlaceMarkerPopup';
import { EventMarkerPopup } from './map/EventMarkerPopup';
import { MapFiltersPanel } from './map/MapFiltersPanel';
import { MapSidebar } from './map/MapSidebar';
import { ClusteredMarkerLayer } from './map/ClusteredMarkerLayer';
import { useMediaQuery } from '@/lib/hooks/use-media-query';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { MobileEntityModal } from '@/components/passage/map/MobileEntityModal';

interface PassageMapInnerProps {
    passageId: string;
    passage?: Passage;
    placesByPassage?: PlacesByPassage;
    historicalEventsByPassage?: HistoricalEventsByPassage;
    historicalEventsByBook?: HistoricalEventsByBook;
    historicalEvents: HistoricalEvent[];
    allPlaces: Place[];
    isGeoAnimating?: boolean;
    geoStage?: TransitionStage;
    geoMapCenter?: { lat: number; lng: number } | null;
    setGeoMarkerPosition?: (pos: { x: number; y: number }) => void;
}

interface Props {
    passageId: string;
    isGeoAnimating?: boolean;
    geoStage?: TransitionStage;
    geoMapCenter?: { lat: number; lng: number } | null;
    setGeoMarkerPosition?: (pos: { x: number; y: number }) => void;
}

interface PlaceMarkerData {
    entity: GeographicEntityWithRelevance;
    position: LatLngExpression;
}

interface EventMarkerData {
    event: HistoricalEventByPassage | HistoricalEvent;
    position: LatLngExpression;
    matchingPlace: GeographicEntity;
}

// Wrapper: ensures all data is loaded before mounting the map component
export default function PassageMap({ passageId, isGeoAnimating, geoStage, geoMapCenter, setGeoMarkerPosition }: Props) {
    const appDataContext = use(AppDataContext);
    const { data: placesByPassage, isLoading: placesLoading } = usePlacesByPassageData(passageId);
    const { data: historicalEventsByPassage, isLoading: eventsLoading } = useHistoricalEventsByPassageData(passageId);
    const historicalEventsByBookContext = use(HistoricalEventsByBookDataContext);
    const historicalEventsContext = use(HistoricalEventsDataContext);
    const passageContext = use(PassageDataContext);
    const placesContext = use(PlacesDataContext);

    const isDataLoaded = appDataContext.data &&
        !placesLoading &&
        passageContext.data &&
        !eventsLoading &&
        historicalEventsByBookContext &&
        historicalEventsContext &&
        placesContext &&
        appDataContext.data.historicalEvents;

    if (!isDataLoaded) {
        return <LoadingSkeleton />;
    }

    // Extract all data needed by inner component
    const passage = passageContext.data!.items.find(p => p.id === passageId);
    const historicalEventsByBook = passage ? historicalEventsByBookContext.getHistoricalEventsByBookByBook(passage.book) : undefined;
    const historicalEvents = appDataContext.data!.historicalEvents || [];
    const allPlaces = appDataContext.data!.places || [];

    return (
        <PassageMapInner
            passageId={passageId}
            passage={passage}
            placesByPassage={placesByPassage}
            historicalEventsByPassage={historicalEventsByPassage}
            historicalEventsByBook={historicalEventsByBook}
            historicalEvents={historicalEvents}
            allPlaces={allPlaces}
            isGeoAnimating={isGeoAnimating}
            geoStage={geoStage}
            geoMapCenter={geoMapCenter}
            setGeoMarkerPosition={setGeoMarkerPosition}
        />
    );
}

function PassageMapInner({
    passageId,
    passage,
    placesByPassage,
    historicalEventsByPassage,
    historicalEventsByBook,
    historicalEvents,
    allPlaces,
    isGeoAnimating,
    geoStage,
    geoMapCenter,
    setGeoMarkerPosition
}: PassageMapInnerProps) {
    const isDarkMode = useDarkMode();
    const [, setUrl] = useUrl();
    // Determine initial year for timeline slider (priority order)
    const passageDate = useMemo(() =>
        calculatePassageDate(passage, historicalEvents, historicalEventsByPassage, historicalEventsByBook),
        [passage, historicalEvents, historicalEventsByPassage, historicalEventsByBook]
    );

    // UI State
    // Sidebar: always open on desktop, closed by default on mobile
    const [sidebarOpen, setSidebarOpen] = useState(typeof window !== 'undefined' && window.innerWidth >= 768);
    const [selectedStyleId, setSelectedStyleId] = useState('watercolor');
    const [showLabels, setShowLabels] = useState(true);
    const [showAncientRoads, setShowAncientRoads] = useState(false);
    const [showSeaLanes, setShowSeaLanes] = useState(false);
    const [showRomanProvinces, setShowRomanProvinces] = useState(false);
    const [showAncientEmpires, setShowAncientEmpires] = useState(false);
    // Renamed from showAllPlaces - default TRUE means show ONLY passage events (hide general places)
    // This flips the logic: checked = hide general places, unchecked = show all places
    const [passageEventsOnly, setPassageEventsOnly] = useState(true);
    const [selectedPlaceTypes, setSelectedPlaceTypes] = useState<Set<string>>(new Set());
    const [selectedEventCategories, setSelectedEventCategories] = useState<Set<string>>(new Set());
    const [selectedYear, setSelectedYear] = useState<number>(0);
    const [timelineData, setTimelineData] = useState<{
        minYear: number;
        maxYear: number;
        eras: TimelineEra[];
    } | null>(null);

    // Mobile Modal State
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [selectedEntityForModal, setSelectedEntityForModal] = useState<GeographicEntityWithRelevance | null>(null);
    const [selectedEventForModal, setSelectedEventForModal] = useState<EventMarkerData | null>(null);

    const [mapReady, setMapReady] = useState(false);


    // Map and marker refs for programmatic control
    const mapRef = useRef<LeafletMap | null>(null);
    const markerRefs = useRef<Map<string, LeafletMarker>>(new Map());
    const eventMarkerRefs = useRef<Map<string, LeafletMarker>>(new Map());
    const mapContainerRef = useRef<HTMLDivElement | null>(null);

    // When the map container resizes (e.g., context panel height changes),
    // notify Leaflet so it can reflow tiles and controls correctly.
    useEffect(() => {
        if (!mapReady) return;
        if (typeof window === 'undefined') return;

        const container = mapContainerRef.current;
        const mapInstance = mapRef.current;
        const ResizeObserverCtor = (window as any).ResizeObserver as
            | (new (callback: ResizeObserverCallback) => ResizeObserver)
            | undefined;

        if (!container || !mapInstance || !ResizeObserverCtor) return;

        const observer = new ResizeObserverCtor(() => {
            mapInstance.invalidateSize();
        });

        observer.observe(container);
        return () => {
            observer.disconnect();
        };
    }, [mapReady]);





    // Function to handle map clicks (close sidebar on mobile)
    const handleMapClick = useCallback(() => {
        if (typeof window !== 'undefined' && window.innerWidth < 768) {
            setSidebarOpen(false);
        }
    }, []);

    // Initialize timeline and filters
    useEffect(() => {
        const result = initializeTimelineAndFilters(placesByPassage, historicalEventsByPassage, historicalEventsByBook, passageDate);
        if (result) {
            setTimelineData({ minYear: result.minYear, maxYear: result.maxYear, eras: result.eras });
            setSelectedYear(result.initialYear);
            setSelectedPlaceTypes(result.placeTypes);
            setSelectedEventCategories(result.eventCategories);
        }
    }, [placesByPassage, historicalEventsByPassage, historicalEventsByBook, passageDate]);



    // Convert relevant places to GeographicEntity format with relevance metadata
    // Only compute when passageEventsOnly is false (meaning "show more places" is active) to avoid unnecessary processing
    const generalPlacesAsEntities = useMemo((): GeographicEntityWithRelevance[] => {
        // If passageEventsOnly is true, we act like "showAllPlaces" is false -> return empty
        if (passageEventsOnly || !passage) return [];


        // Get event titles from passage/book-level events
        const eventTitles = getRelevantEventTitles(historicalEventsByPassage, historicalEventsByBook);

        return allPlaces
            .filter(place => place.coordinates)
            .map(place => {
                // Determine relevance type (for visual differentiation)
                let relevanceType: 'event-related' | 'general';
                let relevanceReason: string;

                // 1. Check if place is connected via relatedHistoricalEvents
                if (place.relatedHistoricalEvents?.some(eventTitle => eventTitles.has(eventTitle))) {
                    relevanceType = 'event-related';
                    relevanceReason = 'Connected via historical event';
                }
                else {
                    // 2. All others are 'general' when passageEventsOnly is disabled
                    relevanceType = 'general';
                    relevanceReason = 'Ancient place';

                    // Note: logic inverted - showAllPlaces=true corresponds to passageEventsOnly=false
                }

                // Use shared utility to convert Place to entity structure
                const baseEntity = convertPlaceToEntity(place, relevanceReason);

                return {
                    ...baseEntity,
                    type: place.type as Place['type'],
                    importance: 'low' as Importance, // Default for non-passage places
                    relevanceType,
                    relevanceReason
                };
            });
    }, [passageEventsOnly, passage, allPlaces, historicalEventsByPassage, historicalEventsByBook]);

    // Update place type filters when general places are added
    useEffect(() => {
        // If passageEventsOnly is false (showing more places)
        if (!passageEventsOnly && generalPlacesAsEntities.length > 0) {
            const generalPlaceTypes = new Set(generalPlacesAsEntities.map(e => e.type));
            setSelectedPlaceTypes(prev => {
                const updated = new Set(prev);
                generalPlaceTypes.forEach(type => updated.add(type));
                return updated;
            });
        }
    }, [passageEventsOnly, generalPlacesAsEntities]);

    // Filter places and events by timeline and categories
    const { visiblePlaces, visibleEvents, placeTypeCounts, eventCategoryCounts } = useMemo(() => {
        return filterMapData({
            placesByPassage,
            historicalEventsByPassage,
            historicalEventsByBook,
            generalPlacesAsEntities,
            selectedYear,
            selectedPlaceTypes,
            selectedEventCategories,
            allHistoricalEvents: historicalEvents,
            passageEventsOnly
        });
    }, [placesByPassage, historicalEventsByPassage, historicalEventsByBook, generalPlacesAsEntities, selectedYear, selectedPlaceTypes, selectedEventCategories, historicalEvents, passageEventsOnly]);



    // Calculate initial map position - prioritize high-importance places
    const { initialCenter, initialBounds } = useMemo(() => {
        return calculateInitialMapPosition(placesByPassage);
    }, [placesByPassage]);

    // Create marker data - split into passage-related (unclustered) and general (clustered)
    const { passageMarkers, generalMarkers } = useMemo(() => {
        const passage: PlaceMarkerData[] = [];
        const general: PlaceMarkerData[] = [];

        visiblePlaces.forEach(entity => {
            const markerData = {
                entity,
                position: [entity.coordinates.latitude, entity.coordinates.longitude] as LatLngExpression
            };

            // General places go into clustered layer for performance
            if (entity.relevanceType === 'general') {
                general.push(markerData);
            } else {
                passage.push(markerData);
            }
        });

        return { passageMarkers: passage, generalMarkers: general };
    }, [visiblePlaces]);

    // Keep placeMarkers for backwards compatibility with sidebar lists
    const placeMarkers: PlaceMarkerData[] = useMemo(() => {
        return [...passageMarkers, ...generalMarkers];
    }, [passageMarkers, generalMarkers]);

    // Create place name lookup for efficient event-to-place matching
    const placeNameLookup = useMemo(() => {
        const lookup = new Map<string, GeographicEntity>();
        placesByPassage?.entities.forEach(entity => {
            lookup.set(entity.placeName.toLowerCase(), entity);
        });
        return lookup;
    }, [placesByPassage]);

    const eventMarkers: EventMarkerData[] = useMemo(() => {
        return visibleEvents
            .map(event => {
                // Try to find a matching place for this event using the lookup Map
                const eventTitleLower = event.title.toLowerCase();
                let matchingPlace: GeographicEntity | undefined;

                // Check if any place name is included in the event title
                for (const [placeName, entity] of placeNameLookup) {
                    if (eventTitleLower.includes(placeName)) {
                        matchingPlace = entity;
                        break;
                    }
                }

                if (!matchingPlace) return null;

                return {
                    event,
                    position: [matchingPlace.coordinates.latitude, matchingPlace.coordinates.longitude] as LatLngExpression,
                    matchingPlace
                };
            })
            .filter((marker): marker is NonNullable<typeof marker> => marker !== null);
    }, [visibleEvents, placeNameLookup]);

    // Generic function to center map on a marker and open its popup
    const handleMarkerClick = useCallback((
        markerName: string,
        coordinates: { latitude: number; longitude: number },
        markerRefsMap: { current: Map<string, LeafletMarker> },
        markerData?: PlaceMarkerData | EventMarkerData
    ) => {
        if (mapRef.current) {
            // Center the map on the marker (without changing zoom)
            mapRef.current.panTo([coordinates.latitude, coordinates.longitude], {
                animate: true,
                duration: 0.5
            });

            // On mobile, show modal instead of popup
            if (isMobile && markerData) {
                if ('entity' in markerData) {
                    setSelectedEntityForModal(markerData.entity);
                } else if ('event' in markerData) {
                    setSelectedEventForModal(markerData);
                }
            } else {
                // Open the marker's popup if it exists
                const marker = markerRefsMap.current.get(markerName);
                if (marker) {
                    setTimeout(() => {
                        marker.openPopup();
                    }, MARKER_POPUP_ANIMATION_DELAY); // Wait for animation to complete
                }
            }

            // Close sidebar on mobile
            if (isMobile) {
                setSidebarOpen(false);
            }
        }
    }, [isMobile]);

    // Convenience wrappers for place and event clicks
    const handlePlaceClick = useCallback((placeName: string, coordinates: { latitude: number; longitude: number }) => {
        const markerData = placeMarkers.find(m => m.entity.placeName === placeName);
        handleMarkerClick(placeName, coordinates, markerRefs, markerData);
    }, [handleMarkerClick, placeMarkers]);

    const handleEventClick = useCallback((eventName: string, coordinates: { latitude: number; longitude: number }) => {
        const markerData = eventMarkers.find(e => e.event.title === eventName);
        handleMarkerClick(eventName, coordinates, eventMarkerRefs, markerData);
    }, [handleMarkerClick, eventMarkers]);

    // Handle 'highlight' query parameter for automatic marker selection
    useEffect(() => {
        if (!mapReady || !visiblePlaces.length) return;

        const params = new URLSearchParams(window.location.search);
        const highlight = params.get('highlight');

        if (highlight) {
            const place = visiblePlaces.find(p => p.placeName.toLowerCase() === highlight.toLowerCase());
            if (place) {
                // Small delay to ensure makers are fully rendered and registered in refs
                const timer = setTimeout(() => {
                    handlePlaceClick(place.placeName, place.coordinates);

                    // Clear the highlight parameter from the URL without reloading to avoid re-triggering
                    const newUrl = window.location.pathname;
                    window.history.replaceState({}, '', newUrl);
                }, 800);
                return () => clearTimeout(timer);
            }
        }
    }, [mapReady, visiblePlaces, handlePlaceClick]);

    // Get current map style
    const currentStyle = MAP_STYLES.find(s => s.id === selectedStyleId) || MAP_STYLES[1];

    // Get available types and categories (dynamic based on passageEventsOnly filter)
    const availablePlaceTypes = useMemo(() => {
        if (!placesByPassage) return [];
        const types = new Set(placesByPassage.entities.map(e => e.type));

        // If not in "passage events only" mode, include types from the wider world
        if (!passageEventsOnly) {
            generalPlacesAsEntities.forEach(e => types.add(e.type));
        }

        return Array.from(types);
    }, [placesByPassage, passageEventsOnly, generalPlacesAsEntities]);

    const availableEventCategories = useMemo(() => {
        // Pool of events to check depends on the filter
        const eventPool = passageEventsOnly
            ? combinePassageAndBookEvents(historicalEventsByPassage, historicalEventsByBook)
            : (historicalEvents || []);

        if (eventPool.length === 0) return [];

        const categories = new Set<string>();
        eventPool.forEach(event => {
            if (event.categories) {
                event.categories.forEach(cat => categories.add(cat.category));
            }
        });
        return Array.from(categories);
    }, [historicalEventsByPassage, historicalEventsByBook, historicalEvents, passageEventsOnly]);

    // Fly-out effect: Wait for zoom-out stage to begin map movement
    useEffect(() => {
        // We trigger the map move at the *start* of the zoom-out phase, 
        // to coordinate with the ruins image shrinking.
        if (geoStage === 'zoom-out' && mapRef.current && initialBounds) {
            // Report where the "marker" is currently (approx center of bounds)
            // This allows the overlay image to shrink towards the map location
            const mapContainer = mapRef.current.getContainer();
            const rect = mapContainer.getBoundingClientRect();
            setGeoMarkerPosition({
                x: rect.left + rect.width / 2,
                y: rect.top + rect.height / 2
            });

            // "Fly out" from specific site to the passage bounds
            mapRef.current.flyToBounds(initialBounds, {
                duration: 1.5,
                easeLinearity: 0.25,
                padding: [50, 50]
            });
        }
    }, [geoStage, initialBounds, setGeoMarkerPosition]);

    // Navigation-fly effect: If we change passages while already in map view,
    // smoothly fly to the new location (skipping cinematic intro).
    useEffect(() => {
        if (mapReady && mapRef.current && initialBounds && !isGeoAnimating) {
            mapRef.current.flyToBounds(initialBounds, {
                duration: 1.2,
                easeLinearity: 0.25,
                padding: [50, 50]
            });
        }
    }, [initialBounds, isGeoAnimating, mapReady]);

    if (!placesByPassage) {
        return <EmptyState passageId={passageId} />;
    }

    if (!timelineData) {
        return <LoadingSkeleton />;
    }

    if (!historicalEventsByPassage?.passageDate) {
        return <LoadingSkeleton />;
    }

    return (
        <div className="relative h-full max-h-[calc(100vh-var(--header-height))] overflow-hidden flex flex-col">
            {/* Map and Content Container - Map fills background */}
            <div className="flex-1 flex overflow-hidden min-h-0 relative">
                {/* Persistent Sidebar (Desktop) */}
                <MapSidebar
                    isOpen={sidebarOpen}
                    selectedStyleId={selectedStyleId}
                    onStyleChange={setSelectedStyleId}
                    showLabels={showLabels}
                    onShowLabelsChange={setShowLabels}
                    showAncientRoads={showAncientRoads}
                    onShowAncientRoadsChange={setShowAncientRoads}
                    showSeaLanes={showSeaLanes}
                    onShowSeaLanesChange={setShowSeaLanes}
                    showRomanProvinces={showRomanProvinces}
                    onShowRomanProvincesChange={setShowRomanProvinces}
                    showAncientEmpires={showAncientEmpires}
                    onShowAncientEmpiresChange={setShowAncientEmpires}
                    passageEventsOnly={passageEventsOnly}
                    onPassageEventsOnlyChange={setPassageEventsOnly}
                    availablePlaceTypes={availablePlaceTypes}
                    selectedPlaceTypes={selectedPlaceTypes}
                    onPlaceTypesChange={setSelectedPlaceTypes}
                    placeTypeCounts={placeTypeCounts}
                    availableEventCategories={availableEventCategories}
                    selectedEventCategories={selectedEventCategories}
                    onEventCategoriesChange={setSelectedEventCategories}
                    eventCategoryCounts={eventCategoryCounts}
                    visiblePlaces={visiblePlaces}
                    eventMarkers={eventMarkers}
                    onPlaceClick={handlePlaceClick}
                    onEventClick={handleEventClick}
                    passageYear={selectedYear}
                />

                {/* Map */}
                <div className="flex-1 relative" ref={mapContainerRef}>
                    <MapContainer
                        center={isGeoAnimating && geoMapCenter ? [geoMapCenter.lat, geoMapCenter.lng] : initialCenter}
                        zoom={isGeoAnimating ? 15 : 8} // Start tight on the site
                        style={{ height: '100%', width: '100%', zIndex: 0 }}
                        zoomControl={false}
                        attributionControl={false}
                        ref={mapRef}
                        whenReady={() => {
                            setMapReady(true);
                        }}
                        // Disable zoom animation for the MapBoundsUpdater initial set
                        // to prevent conflicts with our flyToBounds
                        zoomAnimation={!isGeoAnimating}
                    >
                        <TileLayer
                            url={currentStyle.url}
                            attribution={currentStyle.attribution}
                        />

                        {isGeoAnimating && geoMapCenter && (
                            <MapViewSetter center={geoMapCenter} zoom={15} />
                        )}
                        <MapBoundsUpdater bounds={initialBounds} isAnimating={isGeoAnimating} />
                        <MapColorFilter colorScheme="ancient" />
                        <MapClickHandler onMapClick={handleMapClick} />
                        <MapAnimationStopper />
                        <AncientEmpiresLayer show={showAncientEmpires} selectedYear={selectedYear} />
                        <RomanProvincesLayer show={showRomanProvinces} selectedYear={selectedYear} />
                        <AncientRoadsLayer show={showAncientRoads} showSeaLanes={showSeaLanes} selectedYear={selectedYear} />

                        {/* Clustered general place markers (for performance) */}
                        <ClusteredMarkerLayer
                            markers={generalMarkers}
                            bookName={passage?.book}
                            passageYear={selectedYear}
                            markerRefs={markerRefs}
                            isMobile={isMobile}
                            onMobileMarkerClick={setSelectedEntityForModal}
                            showLabels={showLabels}
                        />

                        {/* Passage-related place markers (unclustered for visibility) */}
                        {passageMarkers.map((marker, index) => (
                            <Marker
                                key={`place-${index}`}
                                position={marker.position}
                                icon={createMemoizedPlaceIcon(marker.entity)}
                                ref={(ref) => {
                                    if (ref) {
                                        markerRefs.current.set(marker.entity.placeName, ref);
                                    }
                                }}
                                eventHandlers={{
                                    click: () => {
                                        if (isMobile) {
                                            setSelectedEntityForModal(marker.entity);
                                        }
                                    }
                                }}
                            >
                                {!isMobile && (
                                    <Popup maxWidth={320} maxHeight={400} offset={[0, -10]}>
                                        <PlaceMarkerPopup entity={marker.entity} bookName={passage?.book} passageYear={selectedYear} />
                                    </Popup>
                                )}
                                {showLabels && !['region', 'body-of-water', 'desert'].includes(marker.entity.type) && (
                                    <Tooltip
                                        permanent
                                        direction="top"
                                        offset={[0, -20]}
                                        opacity={0.9}
                                    >
                                        <div style={{
                                            maxWidth: '180px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            <div style={{
                                                fontWeight: 600,
                                                fontSize: '12px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis'
                                            }}>
                                                {marker.entity.placeName}
                                            </div>
                                        </div>
                                    </Tooltip>
                                )}
                            </Marker>
                        ))}

                        {/* Event markers */}
                        {eventMarkers.map((marker, index) => (
                            <Marker
                                key={`event-${index}`}
                                position={marker.position}
                                icon={createEventIcon()}
                                ref={(ref) => {
                                    if (ref) {
                                        eventMarkerRefs.current.set(marker.event.title, ref);
                                    }
                                }}
                                eventHandlers={{
                                    click: () => {
                                        if (isMobile) {
                                            setSelectedEventForModal(marker);
                                        }
                                    }
                                }}
                            >
                                {!isMobile && (
                                    <Popup maxWidth={300} offset={[0, -10]}>
                                        <EventMarkerPopup
                                            event={marker.event}
                                            historicalEvents={historicalEvents}
                                            bookName={passage?.book}
                                            matchingPlace={marker.matchingPlace}
                                            passageYear={selectedYear}
                                        />
                                    </Popup>
                                )}
                            </Marker>
                        ))}
                    </MapContainer>

                    {/* Control Stack (Top Right - Under the Close button) */}
                    <div className="absolute top-17 right-4 z-[500] flex flex-col gap-3 transition-all duration-300">
                        {/* Filters toggle button - Hidden on desktop as it has persistent sidebar */}
                        <div className="relative md:hidden">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setSidebarOpen(!sidebarOpen)}
                                className={cn(
                                    "h-10 w-10 rounded-full backdrop-blur-md border shadow-lg transition-all",
                                    sidebarOpen
                                        ? isDarkMode
                                            ? "bg-black/85 border-primary text-primary shadow-primary/20"
                                            : "bg-white/95 border-primary text-primary shadow-primary/20"
                                        : isDarkMode
                                            ? "bg-black/70 border-white/20 text-white hover:bg-black/80"
                                            : "bg-white/90 border-black/10 text-slate-900 hover:bg-white"
                                )}
                                title="Filters"
                            >
                                <SlidersHorizontal className={cn(
                                    "h-4 w-4",
                                    sidebarOpen ? "text-primary" : (isDarkMode ? "text-white/70" : "text-slate-900")
                                )} />
                            </Button>

                            {/* Floating Filters Panel (Mobile) */}
                            <MapFiltersPanel
                                isOpen={sidebarOpen}
                                onClose={() => setSidebarOpen(false)}
                                selectedStyleId={selectedStyleId}
                                onStyleChange={setSelectedStyleId}
                                showLabels={showLabels}
                                onShowLabelsChange={setShowLabels}
                                showAncientRoads={showAncientRoads}
                                onShowAncientRoadsChange={setShowAncientRoads}
                                showSeaLanes={showSeaLanes}
                                onShowSeaLanesChange={setShowSeaLanes}
                                showRomanProvinces={showRomanProvinces}
                                onShowRomanProvincesChange={setShowRomanProvinces}
                                showAncientEmpires={showAncientEmpires}
                                onShowAncientEmpiresChange={setShowAncientEmpires}
                                passageEventsOnly={passageEventsOnly}
                                onPassageEventsOnlyChange={setPassageEventsOnly}
                                availablePlaceTypes={availablePlaceTypes}
                                selectedPlaceTypes={selectedPlaceTypes}
                                onPlaceTypesChange={setSelectedPlaceTypes}
                                placeTypeCounts={placeTypeCounts}
                                availableEventCategories={availableEventCategories}
                                selectedEventCategories={selectedEventCategories}
                                onEventCategoriesChange={setSelectedEventCategories}
                                eventCategoryCounts={eventCategoryCounts}
                                visiblePlaces={visiblePlaces}
                                eventMarkers={eventMarkers}
                                onPlaceClick={handlePlaceClick}
                                onEventClick={handleEventClick}
                                passageYear={selectedYear}
                            />
                        </div>

                        {/* Legend toggle button */}
                        <MapLegend
                            activePlaceTypes={Array.from(selectedPlaceTypes)}
                            activeEventCategories={Array.from(selectedEventCategories)}
                            showRelevanceLegend={true}
                            showAncientRoads={showAncientRoads}
                            showRomanProvinces={showRomanProvinces}
                            showAncientEmpires={showAncientEmpires}
                            attributions={currentStyle.attributions}
                        />
                    </div>

                    {/* Timeline control - floating */}
                    <MapTimelineControl
                        minYear={timelineData.minYear}
                        maxYear={timelineData.maxYear}
                        currentYear={selectedYear}
                        eras={timelineData.eras}
                        onYearChange={setSelectedYear}
                        passageDate={historicalEventsByPassage?.passageDate}
                        alternativeDates={historicalEventsByPassage?.alternativeDates}
                    />
                </div>
            </div>


            {/* Mobile Selection Modals */}
            <MobileEntityModal
                entity={selectedEntityForModal}
                eventMarker={selectedEventForModal}
                onClose={() => {
                    setSelectedEntityForModal(null);
                    setSelectedEventForModal(null);
                }}
                bookName={passage?.book}
                passageYear={selectedYear}
                historicalEvents={historicalEvents}
            />
        </div>
    );
}

function LoadingSkeleton() {
    return (
        <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-4">
                <Skeleton className="h-12 w-12 rounded-full mx-auto" />
                <Skeleton className="h-4 w-48" />
            </div>
        </div>
    );
}

function EmptyState({ passageId }: { passageId: string }) {
    const [, setUrl] = useUrl();

    return (
        <div className="flex flex-col h-screen">
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center space-y-4">
                    <MapPin className="h-16 w-16 text-muted-foreground mx-auto" />
                    <h2 className="text-xl font-semibold">No Places to Display</h2>
                    <p className="text-muted-foreground max-w-md">
                        No geographic entities have been identified for this passage.
                        The map requires location data to display.
                    </p>
                    <Button onClick={() => setUrl(`/app/passage/${passageId}/context/places`)}>
                        Back to Places
                    </Button>
                </div>
            </div>
        </div>
    );
}
